import { Injectable } from '@angular/core';
import { ChronoField, DateTimeFormatter, DayOfWeek, LocalDate, LocalDateTime, ZonedDateTime, ZoneId} from '@js-joda/core';
import '@js-joda/timezone';
import isEmpty from 'lodash-es/isEmpty';

export enum UtcDateTimeStringBrand { }
export type UtcDateTimeString = string & UtcDateTimeStringBrand;

@Injectable({
  providedIn: 'root'
})
export class SLTDateTimeHelperService {
  static readonly DATE_FORMAT = 'yyyy-MM-dd';
  static readonly READABLE_DATE_FORMAT = 'MM/dd';
  static readonly TRAIN_DATE_FORMAT = 'yyyyMMdd';
  static readonly MM_DD_YYYY_HH_MM_SS_FORMAT = 'MM/dd/yyyy HH:mm:ss';
  static readonly MMDDYYYY_FORMAT = 'MM/dd/yyyy';
  static readonly CENTRAL_TIMEZONE = 'America/Chicago';
  static readonly ORDER_DATE_TIME_FORMAT = 'MM/dd/yyyy HH:mm:ss';
  static readonly ORDER_DATE_DDMMYY_FORMAT = 'MM/dd/yyyy';


  utcToCentralTime(utcDateString: string, format = SLTDateTimeHelperService.MMDDYYYY_FORMAT) {
    return this.convertUTCtoZoneDateTime(utcDateString, SLTDateTimeHelperService.CENTRAL_TIMEZONE, format);
  }

  public convertUTCtoZoneDateTime(utcDateString: string,
    zoneId: string,
    format = SLTDateTimeHelperService.MM_DD_YYYY_HH_MM_SS_FORMAT): string {
    if (!utcDateString) {
      return '';
    }
    const dateStr = utcDateString
      .toString()
      .trim()
      .toLowerCase()
      .endsWith('z')
      ? utcDateString.substring(0, utcDateString.length - 1)
      : utcDateString;
    const date = new Date(dateStr);

    const localDateTime = LocalDateTime.of(date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds());

    const zonedDateTimeString = ZonedDateTime.of(
      localDateTime,
      ZoneId.of(zoneId)
    );
    const jsDate = zonedDateTimeString
      .plusSeconds(zonedDateTimeString.offset().totalSeconds());

    return jsDate.format(DateTimeFormatter.ofPattern(format));
  }

  public getCurrentDate() {
    return this.getZonedDateString(LocalDateTime.now().toString());
  }

  public getUtcDateTime(
    dateString: string,
    timeString: string,
    zoneString: string
  ) {
    const zonedDateTimeString = this.combineIntoZonedDateTime(
      dateString,
      timeString,
      zoneString
    );
    return this.convertFromZonedToUTC(
      this.convertFromZonedToUTC(zonedDateTimeString)
    );
  }
  public getZonedDateStringFromUtc(
    zoneString: string,
    utcDateTimeString: string
  ) {
    const zonedDateTimeString = this.convertFromUTCToZoned(
      zoneString,
      utcDateTimeString
    );
    return this.getZonedDateString(zonedDateTimeString);
  }

  public getZonedTimeStringFromUtc(
    zoneString: string,
    utcDateTimeString: string
  ) {
    const zonedDateTimeString = this.convertFromUTCToZoned(
      zoneString,
      utcDateTimeString
    );
    return this.getZonedTimeString(zonedDateTimeString);
  }

  public getDateOfDayInSameWeek(
    nameOfDay: string,
    dateTimeWithinSameWeek: string
  ) {
    const localDate = LocalDate.parse(dateTimeWithinSameWeek);
    const dayEnum = DayOfWeek.valueOf(nameOfDay.toUpperCase());
    const targetDate = localDate.with(ChronoField.DAY_OF_WEEK, dayEnum.value());
    const shortDateString = targetDate.format(
      DateTimeFormatter.ofPattern(SLTDateTimeHelperService.DATE_FORMAT)
    );

    return shortDateString;
  }

  public getDatePlusDays(dateString: string, days: number) {
    const futureDate = LocalDate.parse(dateString.substring(0, 10)).plusDays(
      days
    );
    const formatter = DateTimeFormatter.ofPattern(SLTDateTimeHelperService.DATE_FORMAT);
    return formatter.format(futureDate);
  }

  public getDateMinusDays(dateString: string, days: number): string {
    const pastDate = LocalDate.parse(dateString.substring(0, 10)).minusDays(
      days
    );
    const formatter = DateTimeFormatter.ofPattern(SLTDateTimeHelperService.DATE_FORMAT);
    return formatter.format(pastDate);
  }
  public getCurrentTimeInTimeZone(zoneId: string) {
    return ZonedDateTime.now().withZoneSameInstant(ZoneId.of(zoneId));
  }

  private isValidDateString(str: string) {
    if (isEmpty(str)) {
      return false;
    }
    // TODO validation
    return true;
  }

  private isValidTimeString(str: string) {
    if (isEmpty(str)) {
      return false;
    }
    // TODO validation
    return true;
  }

  private isValidZoneString(str: string) {
    if (isEmpty(str)) {
      return false;
    }
    // TODO validation
    return true;
  }

  private isValidZonedDateTimeString(str: string) {
    if (isEmpty(str)) {
      return false;
    }
    // TODO validation
    return true;
  }

  private isValidUtcDateTimeString(str: string) {
    if (isEmpty(str)) {
      return false;
    }
    // TODO validation
    return true;
  }

  private combineIntoZonedDateTime(
    dateString: string,
    timeString: string,
    zoneString: string
  ) {
    // These are "magic" if statements in typescript, they transform the types of the things within them
    // For further reading Google Typescript Type Guards
    // https://www.typescriptlang.org/docs/handbook/advanced-types.html
    if (
      this.isValidDateString(dateString) &&
      this.isValidTimeString(timeString) &&
      this.isValidZoneString(zoneString)
    ) {
      // Since validation passed:
      // dateString is a DateString
      // timeString is a TimeString
      // zoneString is a ZoneString

      // https://js-joda.github.io/js-joda/cheat-sheet.html#zoneddatetime
      // ZonedDateTime.of(LocalDateTime.parse('2012-06-06T00:00'), ZoneId.of('Europe/Paris')); // 2012-06-06T00:00+02:00[Europe/Paris]
      const localDateTime = LocalDateTime.parse(`${dateString}T${timeString}`);
      const zoneId = ZoneId.of(zoneString);
      const zonedDateTimeString: ZonedDateTime = localDateTime.atZone(zoneId);
      return zonedDateTimeString.toString();
    } else {
      throw new Error(`Validation failed,
          ${dateString} is valid date string? ${this.isValidDateString(
        dateString
      )}
          ${timeString} is valid time string? ${this.isValidTimeString(
        timeString
      )}
          ${zoneString} is valid zone string? ${this.isValidZoneString(
        zoneString
      )}`);
    }
  }

  public getZonedDateString(zonedDateTimeString: string) {
    if (this.isValidZonedDateTimeString(zonedDateTimeString)) {
      // split the string to the date characters
      // 2016-03-18T16:00-4:00[America/New_York]
      return zonedDateTimeString.substring(0, 10);
    } else {
      throw new Error(
        `Validation failed, ${zonedDateTimeString} is not a valid ZonedDateTimeString`
      );
    }
  }

  private getZonedTimeString(zonedDateTimeString: string) {
    if (this.isValidZonedDateTimeString(zonedDateTimeString)) {
      // split the string to the time characters
      // 2016-03-18T16:00-4:00[America/New_York]
      return zonedDateTimeString.substring(11, 16);
    } else {
      throw new Error(
        `Validation failed, ${zonedDateTimeString} is not a valid ZonedDateTimeString`
      );
    }
  }

/*   private getZoneString(zonedDateTimeString: string) {
    if (this.isValidZonedDateTimeString(zonedDateTimeString)) {
      // split the string to the zone characters (everything inside square brackets)
      // 2016-03-18T16:00-4:00[America/New_York]
      const startPosition = zonedDateTimeString.indexOf('[') + 1;
      const endPosition = zonedDateTimeString.indexOf(']', startPosition);
      return zonedDateTimeString.substring(
        startPosition,
        endPosition
      );
    } else {
      throw new Error(
        `Validation failed, ${zonedDateTimeString} is not a valid ZonedDateTimeString`
      );
    }
  } */

  private convertFromZonedToUTC(
    zonedDateTimeString: string
  ) {
    if (this.isValidZonedDateTimeString(zonedDateTimeString)) {
      // https://js-joda.github.io/js-joda/cheat-sheet.html#zoneddatetime
      return ZonedDateTime.parse(zonedDateTimeString)
        .withZoneSameInstant(ZoneId.of("UTC"))
        .toString();
    } else {
      throw new Error(
        `Validation failed, ${zonedDateTimeString} is not a valid ZonedDateTimeString`
      );
    }
  }

  private convertFromUTCToZoned(
    zoneString: string,
    utcDateTimeString: string
  ) {
    if (
      this.isValidUtcDateTimeString(utcDateTimeString) &&
      this.isValidZoneString(zoneString)
    ) {
      // https://js-joda.github.io/js-joda/cheat-sheet.html#zoneddatetime
      return ZonedDateTime.parse(utcDateTimeString)
        .withZoneSameInstant(ZoneId.of(zoneString))
        .toString();
    } else {
      throw new Error(`Validation failed,
          ${utcDateTimeString} is a valid utc date time string? ${this.isValidUtcDateTimeString(
        utcDateTimeString
      )}
          ${zoneString} is a valid zone string? ${this.isValidZoneString(
        zoneString
      )}`);
    }
  }
  public getMMDDFormat(aDate: string): string {
    if (isEmpty(aDate)) {
      return '';
    }
    const date = LocalDate.parse(aDate);
    return (
      this.preserveZero(date.monthValue()) +
      '/' +
      this.preserveZero(date.dayOfMonth())
    );
  }

  public getMMDDYYYYFormat(aDate: any): string {
    if ('' === aDate) {
      return '';
    }

    const date = LocalDate.parse(aDate.toISOString().substring(0, 10));
    return (
      this.preserveZero(date.year()) +
      '-' +
      this.preserveZero(date.monthValue()) +
      '-' +
      this.preserveZero(date.dayOfMonth())
    );
  }

  private preserveZero(val: number): string {
    return ('0' + val).slice(-2);
  }
  public convertMMDDYYYYToLocaleDate(date: string): LocalDate {
    const formatter = DateTimeFormatter.ofPattern(
      SLTDateTimeHelperService.TRAIN_DATE_FORMAT
    );
    return LocalDate.parse(date, formatter);
  }
  public calcDiff(firstDate: Date, secondDate: Date) {
    const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
    const diffDays = Math.round(
      Math.abs((firstDate.getTime() - secondDate.getTime()) / oneDay)
    );
    return diffDays;
  }

getDateTime(date: string, time: string): Date | null {
  let dateTime: Date | null = null;

  if (!date && !time) {
    return null;
  }

  if (date) {
    dateTime = this.getExactDate(date); // Assuming this returns a Date
  }

  if (!time) {
    return dateTime;
  }

  const timeSplit = time.split(':');
  if (timeSplit.length === 2 && dateTime) {
    dateTime.setHours(+timeSplit[0]);
    dateTime.setMinutes(+timeSplit[1]);
    return dateTime;
  }

  // Fallback return to satisfy all code paths
  return dateTime;
}

  //https://stackoverflow.com/questions/7556591/is-the-javascript-date-object-always-one-day-off
  getExactDate(dateString: string): Date { // dateString = 2020-10-15
    if (!dateString) { return null };
    const onlyDateString = dateString.substring(0, 10);
    const dateArray = onlyDateString?.split("-");
    if (isEmpty(dateArray) || dateArray.length < 3) { return null };
    const date = new Date(onlyDateString+"T00:00");
  // date.setFullYear(Number(dateArray[0]));
  // date.setMonth(Number(dateArray[1]) - 1);    
	// date.setDate(Number(dateArray[2]));
    const timeString = dateString.substring(11, 16);
    if (timeString) {
      const timeSplit = timeString.split(':');
      if (timeSplit && timeSplit.length === 2 && date) {
        date.setHours(+timeSplit[0]);
        date.setMinutes(+timeSplit[1]);
      }
    }
    return date;
  }


  convertToExactDate(dateString: string): Date { // dateString = 10/15/2023 MM/dd/YYYY
    if (!dateString) { return null };
    const onlyDateString = dateString.substring(0, 10);
    const dateArray = onlyDateString?.split("/");
    if (isEmpty(dateArray) || dateArray.length < 3) { return null };
    const date = new Date(dateString);   
    
    // date.setMonth(Number(dateArray[0]) - 1);
    // date.setDate(Number(dateArray[1]));
    // date.setFullYear(Number(dateArray[2]));
   
    return date;
  }

  getSystemTimeZone() {
    const zone: ZoneId = ZoneId.systemDefault();
    const systmeZone = zone.id();
    console.log(ZoneId.systemDefault().id.name)
    return systmeZone;
  }

  getDateTimeString(date: string, time: string): string | null {
  let dateTime: Date | null = null;
  if (!date && !time) {
    return null;
  }
  if (date) {
    dateTime = new Date(date);
  }
  if (!time) {
    return dateTime?.toISOString() ?? null;
  }
  const timeSplit = time.split(':');
  if (timeSplit && timeSplit.length === 2 && dateTime) {
    dateTime.setHours(+timeSplit[0]);
    dateTime.setMinutes(+timeSplit[1]);
    return dateTime.toISOString();
  }
  return null; // ✅ Ensure all paths return something
}
  getZoneGroupForId() {
    // ZoneId.of() 
    // console.log(DateTimeZone.forID("America/Los_Angeles"))
  }

  getExactDateString(date: Date, format: 'YYYY-dd-MM' | 'MM/dd/YYYY' = 'YYYY-dd-MM'): string { // return date in YYYY-DD-MM format
    let month: any = date.getMonth() + 1;
    month = month < 10 ? '0' + month : month;
    const dayOfMonth = date.getDate() < 10 ? '0' + date.getDate() : date.getDate();
    if (format === 'YYYY-dd-MM') {
      return `${date.getFullYear()}-${month}-${dayOfMonth}`;
    } else {
      return `${month}/${dayOfMonth}/${date.getFullYear()}`;
    }
  }

  getExactDateWithTimeString(date: Date): string { // return date in YYYY-DD-MM format
    let month: any = date.getMonth() + 1;
    month = month < 10 ? '0' + month : month;
    const dayOfMonth = date.getDate() < 10 ? '0' + date.getDate() : date.getDate();
    const hours = date.getHours() < 10 ? '0' + date.getHours() : date.getHours();
    const minutes = date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes();
    return `${date.getFullYear()}-${month}-${dayOfMonth}T${hours}:${minutes}:00.000Z`;
  }

  getTimeAMPMFormat(date: Date): string {
    let hours = date.getHours();
    let minutes: any = date.getMinutes();
    const am_pm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    // appending zero in the start if hours less than 10
    minutes = minutes < 10 ? '0' + minutes : minutes;
    return hours + ':' + minutes + ' ' + am_pm;
  };

  getTime(date: Date): string {
    const hours = date.getHours();
    let minutes: any = date.getMinutes();
    minutes = minutes < 10 ? '0' + minutes : minutes;
    return hours + ':' + minutes;
  }


getExactFormattedDateString(
  dateString: string,
  format: 'YYYY-dd-MM' | 'MM/dd/YYYY' = 'YYYY-dd-MM'
): string | undefined {
  if (!dateString) {
    return undefined;
  }

  const date = this.getExactDate(dateString);
  if (!date) {
    return undefined;
  }

  return this.getExactDateString(date, format);
}

  public getCurrentDateTimeInTimeZone(zoneId: string) {
    if (!zoneId) { return null }
    const currentDateTime = this.getCurrentTimeInTimeZone(zoneId);
    const date = new Date(currentDateTime.year(),
      (currentDateTime.monthValue() - 1),
      currentDateTime.dayOfMonth(),
      currentDateTime.hour(),
      currentDateTime.minute(),
      currentDateTime.second())
    return date;
  }

 getExactDateTimeForZoneFromUTC(timeZone: string, utcDateString: string): Date | undefined {
  if (!timeZone || !utcDateString) {
    return undefined;
  }

  const dateString = this.getZonedDateStringFromUtc(timeZone, utcDateString);
  const timeString = this.getZonedTimeStringFromUtc(timeZone, utcDateString);
  const dateTimeObj = this.getDateTime(dateString, timeString);

  return dateTimeObj;
}

  formattedTime(value: any) {
    let updatedValue = '';
    if (value.length < 5) {
      const timeArray = value.split(':');
      if (timeArray.length === 2) {
        if (timeArray[0].length === 1) {
          updatedValue = `0${timeArray[0]}`;
        } else {
          updatedValue = timeArray[0];
        }

        if (timeArray[1].length === 1) {
          updatedValue = `${updatedValue}:0${timeArray[1]}`;
        } else {
          updatedValue = `${updatedValue}:${timeArray[1]}`;
        }
      }
    } else {
      updatedValue = value;
    }
    return updatedValue;
  }
  formatDateToCustomISO(date: Date): string {
  const pad = (n: number, width = 2) => n.toString().padStart(width, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const milliseconds = pad(date.getMilliseconds(), 3);
  const offsetMinutes = date.getTimezoneOffset(); 
  const offsetSign = offsetMinutes > 0 ? '-' : '+';
  const offsetAbs = Math.abs(offsetMinutes);
  const offsetHours = pad(Math.floor(offsetAbs / 60));
  const offsetMins = pad(offsetAbs % 60);
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetSign}${offsetHours}:${offsetMins}`;
}
 
}
